import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core'

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatOptionModule } from '@angular/material/core'
import { MatButtonModule } from '@angular/material/button'
import { AuthApiService, InitiateMfaResponse } from 'src/api/auth'
import type { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { UsersService } from 'src/services/data/users.service'

type MfaMethod = 'sms' | 'whatsapp' | 'email'

export interface MfaSettingsDialogData {}

interface MfaMethodOption {
  label: string
  value: MfaMethod
}

@Component({
  selector: 'tc-mfa-settings',
  templateUrl: './mfa-settings.component.html',
  styleUrls: ['./mfa-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatButtonModule
],
})
export class MfaSettingsComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder)
  private readonly toaster = inject(ToasterService)
  private readonly authApi = inject(AuthApiService)
  private readonly usersService = inject(UsersService)
  private readonly dialogRef = inject<MatDialogRef<MfaSettingsComponent>>(MatDialogRef)
  private readonly cd = inject(ChangeDetectorRef)

  readonly methods: MfaMethodOption[] = [
    { label: 'SMS', value: 'sms' },
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: 'Email', value: 'email' },
  ]

  readonly methodForm: FormGroup
  readonly verifyForm: FormGroup

  currentMethod?: MfaMethod
  step: 'method' | 'verify' = 'method'
  isInitiating = false
  isResending = false
  isVerifying = false
  resendDisabledSeconds = 0

  changeToken?: string
  destination?: string
  channel?: string
  submitError?: string
  verifyError?: string

  private countdownHandle?: ReturnType<typeof setInterval>
  private defaultMethod: MfaMethod = 'sms'

  constructor() {
    this.methodForm = this.fb.group({
      method: [null, Validators.required],
    })

    this.verifyForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{4,6}$/)]],
    })
  }

  async ngOnInit(): Promise<void> {
    this.currentMethod = await this.getCurrentUserPreference();
    this.methodForm.patchValue({ method: this.currentMethod || this.defaultMethod })
    this.cd.markForCheck()
  }

  ngOnDestroy(): void {
    this.stopCountdown()
  }

  get selectedMethod(): MfaMethod {
    return this.methodForm.get('method')?.value as MfaMethod
  }

  get currentMethodLabel(): string | undefined {
    const method = this.currentMethod
    if (!method) return undefined
    return this.methods.find((option) => option.value === method)?.label
  }

  get isCurrentMethodSelected(): boolean {
    return this.currentMethod === this.selectedMethod
  }

  get channelDisplay(): string {
    switch (this.channel) {
      case 'sms':
      case 'text':
        return 'SMS'
      case 'whatsapp':
        return 'WhatsApp'
      case 'email':
        return 'Email'
      default:
        return this.channel || ''
    }
  }

  async submitMethod(): Promise<void> {
    if (this.methodForm.invalid) {
      this.methodForm.markAllAsTouched()
      return
    }

    const selected = this.selectedMethod
    if (selected === this.currentMethod) {
      this.submitError = `You're already using ${this.currentMethodLabel ?? 'this method'}.`
      this.cd.markForCheck()
      return
    }

    if(!this.authApi.currentUser) {
      this.submitError = 'Unable to load your profile information. Please try again later.'
      this.cd.markForCheck()
      return
    }

    this.submitError = undefined
    this.isInitiating = true
    this.cd.markForCheck()

    try {
      const response = await this.authApi.initiateMfaChange(selected)
      this.handleInitiateResponse(response)
    } catch (error) {
      this.submitError = this.extractErrorMessage(error)
    } finally {
      this.isInitiating = false
      this.cd.markForCheck()
    }
  }

  async resendCode(): Promise<void> {
    if (this.resendDisabledSeconds > 0 || this.isResending) return

    this.verifyError = undefined
    this.isResending = true
    this.cd.markForCheck()

    try {
      const response = await this.authApi.initiateMfaChange(this.selectedMethod)
      this.handleInitiateResponse(response, { stayOnStep: true })
    } catch (error) {
      this.verifyError = this.extractErrorMessage(error)
    } finally {
      this.isResending = false
      this.cd.markForCheck()
    }
  }

  async verifyCode(): Promise<void> {
    if (this.verifyForm.invalid) {
      this.verifyForm.markAllAsTouched()
      return
    }

    if (!this.changeToken) {
      this.verifyError = 'Unable to verify. Please resend the code and try again.'
      this.cd.markForCheck()
      return
    }

    this.verifyError = undefined
    this.isVerifying = true
    this.cd.markForCheck()

    const otp = (this.verifyForm.value.otp as string)?.trim()

    try {
      const response = await this.authApi.verifyMfaChange(this.changeToken, otp)
      if (response?.success) {
        this.currentMethod = this.selectedMethod
        this.toaster.success('MFA method updated successfully.')
        this.dialogRef.close({
          method: this.selectedMethod,
        })
        return
      }
      this.verifyError = 'The code entered is invalid. Please try again.'
    } catch (error) {
      this.verifyError = this.extractErrorMessage(error)
    } finally {
      this.isVerifying = false
      this.cd.markForCheck()
    }
  }

  cancel(): void {
    this.dialogRef.close()
  }

  goBackToMethod(): void {
    this.stopCountdown()
    this.step = 'method'
    this.verifyForm.reset()
    this.changeToken = undefined
    this.destination = undefined
    this.channel = undefined
    this.verifyError = undefined
    this.submitError = undefined
    this.resendDisabledSeconds = 0
    this.cd.markForCheck()
  }

  private handleInitiateResponse(response: InitiateMfaResponse, options?: { stayOnStep?: boolean }): void {
    if (!response?.initiated || !response.change_token) {
      this.submitError = 'Unable to send verification code. Please try again later.'
      this.cd.markForCheck()
      return
    }

    this.changeToken = response.change_token
    this.destination = response.destination
    this.channel = response.channel
    this.submitError = undefined

    this.toaster.success(this.buildVerificationSentMessage())

    const cooldownSeconds = response.resend_cooldown_sec || 60;
    this.startCountdown(cooldownSeconds)

    if (!options?.stayOnStep) {
      this.step = 'verify'
    }

    this.verifyError = undefined
    this.cd.markForCheck()
  }

  private buildVerificationSentMessage(): string {
    if (this.channel && this.destination) {
      return `A verification code has been sent via ${this.channelDisplay} to ${this.destination}`
    }
    if (this.channel) {
      return `A verification code has been sent via ${this.channelDisplay}`
    }
    return 'A verification code has been sent to your registered phone/email'
  }

  private startCountdown(seconds: number): void {
    this.stopCountdown()
    this.resendDisabledSeconds = seconds
    if (seconds <= 0) return

    this.countdownHandle = setInterval(() => {
      this.resendDisabledSeconds = Math.max(0, this.resendDisabledSeconds - 1)
      if (this.resendDisabledSeconds === 0) {
        this.stopCountdown()
      }
      this.cd.markForCheck()
    }, 1000)
  }

  private stopCountdown(): void {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle)
      this.countdownHandle = undefined
    }
    this.resendDisabledSeconds = 0
  }

  private extractCooldownInfo(error: unknown): { remaining?: number; total?: number; retryAfter?: number } {
    const httpErr = error as { error?: any }
    const rawDetails = httpErr?.error?.error?.details ?? httpErr?.error?.details ?? (httpErr as any)?.details
    const details = Array.isArray(rawDetails) ? rawDetails[0] : rawDetails

    const parseSeconds = (value: unknown): number | undefined => {
      const numeric = Number(value)
      return Number.isFinite(numeric) ? Math.max(0, Math.ceil(numeric)) : undefined
    }

    return {
      remaining: parseSeconds(details?.resend_cooldown_remaining_sec),
      total: parseSeconds(details?.resend_cooldown_sec),
      retryAfter: parseSeconds(details?.retry_after_sec),
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (!error) return 'Something went wrong. Please try again.'

    if (typeof error === 'string') return error
    if (error instanceof Error && error.message) return error.message

    const httpError = error as HttpErrorResponse
    const nested = httpError?.error
    const code = nested?.error?.subtypeCode
    const cooldownInfo = this.extractCooldownInfo(httpError)

    switch (code) {
      case 'MISSING_DESTINATION':
        return 'We cannot find contact information for this delivery method. Please contact your administrator.'
      case 'OTP_RESEND_COOLDOWN':
        if (cooldownInfo?.remaining && cooldownInfo?.total) {
          return `Please wait ${cooldownInfo.remaining}s before requesting another verification code. (${cooldownInfo.total}s between requests)`
        } else if (cooldownInfo?.remaining) {
          return `Please wait ${cooldownInfo.remaining}s before requesting another verification code.`
        }
        return 'Please wait before requesting another verification code.'
      case 'OTP_SEND_RATE_LIMITED': {
        const retryAfter = cooldownInfo?.retryAfter ?? cooldownInfo?.remaining
        if (retryAfter && retryAfter > 0) {
          const minutes = Math.max(1, Math.ceil(retryAfter / 60))
          const unit = minutes === 1 ? 'minute' : 'minutes'
          return `Too many requests. Please wait ${minutes} ${unit} before trying again.`
        }
        return 'Too many requests. Please wait before trying again.'
      }
      case 'INVALID_TOKEN':
        return 'Your verification session has expired. Please send the code again.'
      case 'OTP_INVALID':
        return 'Invalid verification code. Please try again.'
      case 'OTP_VERIFY_ATTEMPTS_EXCEEDED':
        return 'Too many verification attempts. Please request a new code.'
      default:
        return nested?.error?.message || 'Something went wrong. Please try again.'
    }
  }

  private async getCurrentUserPreference(): Promise<MfaMethod | null> {
    const authUser = this.authApi.currentUser
    if (!authUser?.account || !authUser?.user_id) {
      console.warn('Failed loading latest profile information, will not display current method.');
      return null;
    }

    try {
      const user = await this.usersService.get(authUser.account, authUser.user_id)
      return user?.preferred_mfa_method || this.defaultMethod;
    } catch (error) {
      console.warn('Failed loading latest profile information, will not display current method.');
      return null;
    }
  }
}
